import type {
  DijkstraOptions,
  DijkstraResult,
  Path,
  Segment,
  TripMode,
} from '@/types';
import { GRAPH, STATIONS } from '@/data/graph';
import { getCost, getDijkstraCost, getUsedMode } from './edges';
import { TRANSFER_MIN } from './constants';

/**
 * Min-heap binaire générique pour la priority queue de Dijkstra.
 *
 * Pourquoi pas l'implémentation naïve (sort à chaque pop) :
 * - Dijkstra est appelé à chaque redraw de la carte, à chaque ouverture de
 *   popup, et n × n fois dans le TSP. Sur 200 nœuds, sort() pèse en O(n log n)
 *   par pop, soit O(n² log n) au total : ~300 000 ops par appel.
 * - Une heap binaire ramène ça à O((V + E) log V) ≈ 1500 ops sur le même graphe.
 * - Gain pratique : TSP à 7 étapes passe de ~30 ms à ~0.5 ms.
 *
 * API minimaliste, juste ce qu'il faut pour Dijkstra :
 *   - push(node) : ajoute un élément
 *   - pop()      : retire et renvoie l'élément de plus petit `d`
 *   - size       : nombre d'éléments restants
 *
 * Le comparateur est sur le champ `d` (distance). Lazy deletion : on n'enlève
 * pas les doublons, on les ignore au pop (cf. `cur.d > dist[cur.id]` côté Dijkstra).
 */
class MinHeap<T extends { d: number }> {
  private items: T[] = [];

  get size(): number {
    return this.items.length;
  }

  push(item: T): void {
    this.items.push(item);
    this.bubbleUp(this.items.length - 1);
  }

  pop(): T | undefined {
    const n = this.items.length;
    if (n === 0) return undefined;
    const top = this.items[0];
    const last = this.items.pop()!;
    if (n > 1) {
      this.items[0] = last;
      this.bubbleDown(0);
    }
    return top;
  }

  private bubbleUp(i: number): void {
    const items = this.items;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (items[i].d < items[parent].d) {
        [items[i], items[parent]] = [items[parent], items[i]];
        i = parent;
      } else break;
    }
  }

  private bubbleDown(i: number): void {
    const items = this.items;
    const n = items.length;
    while (true) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let smallest = i;
      if (l < n && items[l].d < items[smallest].d) smallest = l;
      if (r < n && items[r].d < items[smallest].d) smallest = r;
      if (smallest === i) break;
      [items[i], items[smallest]] = [items[smallest], items[i]];
      i = smallest;
    }
  }
}

/**
 * Calcule les distances depuis `start` vers TOUS les nœuds atteignables,
 * en utilisant Dijkstra sur GRAPH.
 *
 * Renvoie deux distances par nœud :
 * - `dist`      → durée RÉELLE en minutes (pour l'affichage)
 * - `perceived` → durée perçue (avec bonus nuit) — utilisée comme clé de tri
 *
 * Plus des index utiles :
 * - `direct[id]` → true si le nœud est atteint en arête directe depuis `start`
 * - `night[id]`  → true si TOUT le chemin vers ce nœud est en train de nuit
 *
 * La pénalité TRANSFER_MIN est ajoutée sur chaque arête sauf au point de départ.
 */
export function dijkstra(
  start: string,
  mode: TripMode,
  opts: DijkstraOptions = {},
): DijkstraResult {
  const directOnly = opts.directOnly === true;

  const dist: Record<string, number> = {};
  const realDist: Record<string, number> = {};
  const prev: DijkstraResult['prev'] = {};
  const directReach: Record<string, boolean> = {};
  const nightReach: Record<string, boolean> = {};
  // Propage le fait qu'au moins UN segment train de nuit qualifiant (≥ 6h)
  // est présent dans le chemin optimal vers chaque nœud. Permet au mode
  // "Nuit" strict de filtrer les destinations affichables.
  const hasNightTrain: Record<string, boolean> = {};

  STATIONS.forEach((s) => {
    dist[s.id] = Infinity;
    realDist[s.id] = Infinity;
  });
  dist[start] = 0;
  realDist[start] = 0;
  directReach[start] = true;
  nightReach[start] = true;
  hasNightTrain[start] = false; // l'origine n'a parcouru aucun segment

  const queue = new MinHeap<{ id: string; d: number }>();
  queue.push({ id: start, d: 0 });

  while (queue.size > 0) {
    const cur = queue.pop()!;
    // Lazy deletion : si on a déjà trouvé un meilleur chemin entre l'insertion
    // et maintenant, on ignore cette entrée obsolète.
    if (cur.d > dist[cur.id]) continue;

    const neighbors = GRAPH[cur.id];
    for (const nb of neighbors) {
      const realCost = getCost(nb, mode);
      const perceivedCost = getDijkstraCost(nb, mode);
      if (realCost == null || perceivedCost == null) continue;
      if (directOnly && !nb.direct) continue;

      const usedMode = getUsedMode(nb, mode);
      if (!usedMode) continue;

      // Pas de pénalité de correspondance au tout premier saut
      const transfer = cur.id === start ? 0 : TRANSFER_MIN;
      const alt = dist[cur.id] + perceivedCost + transfer;
      const realAlt = realDist[cur.id] + realCost + transfer;

      if (alt < dist[nb.to]) {
        dist[nb.to] = alt;
        realDist[nb.to] = realAlt;
        prev[nb.to] = {
          from: cur.id,
          cost: realCost,
          mode: usedMode,
          direct: nb.direct,
          night: nb.night,
          nightBus: nb.nightBus,
          op: nb.op,
        };
        if (cur.id === start) {
          directReach[nb.to] = nb.direct;
          nightReach[nb.to] = nb.night;
        } else {
          directReach[nb.to] = false;
          nightReach[nb.to] = (nightReach[cur.id] ?? false) && nb.night;
        }

        // hasNightTrain : true si le parent l'avait DÉJÀ, OU si cette arête
        // elle-même est un train de nuit qualifiant (>= 6h sur le rail).
        // Seuil 6h aligné avec NIGHT_SEGMENT_MIN_DURATION dans constants.
        const thisEdgeIsNightTrain =
          usedMode === 'train' &&
          nb.night === true &&
          nb.train != null &&
          nb.train >= 360;
        hasNightTrain[nb.to] =
          (hasNightTrain[cur.id] ?? false) || thisEdgeIsNightTrain;

        queue.push({ id: nb.to, d: alt });
      }
    }
  }

  return {
    dist: realDist,
    perceived: dist,
    prev,
    direct: directReach,
    night: nightReach,
    hasNightTrain,
  };
}

/**
 * Reconstruit le chemin optimal de `start` à `end`.
 * Renvoie `null` si `end` est inatteignable.
 */
export function findPath(
  start: string,
  end: string,
  mode: TripMode,
  opts: DijkstraOptions = {},
): Path | null {
  const result = dijkstra(start, mode, opts);
  if (result.dist[end] === Infinity) return null;

  const segments: Segment[] = [];
  let cur = end;
  while (cur !== start) {
    const p = result.prev[cur];
    if (!p) return null;
    segments.unshift({
      from: p.from,
      to: cur,
      cost: p.cost,
      mode: p.mode,
      direct: p.direct,
      night: p.night,
      nightBus: p.nightBus,
      op: p.op,
    });
    cur = p.from;
  }
  return { segments, total: result.dist[end] };
}
